/*
  <sticky-add-to-cart> — mobile sticky bar that mirrors the main variant
  picker, quantity stepper, total and add-to-cart button. Becomes visible
  via IntersectionObserver when the main submit button has scrolled above
  the viewport.

  All interactions delegate to the existing main UI:
  - Pill tap → set the corresponding main radio + dispatch change. The
    existing pubsub flow updates the variant, prices, total, and the
    sticky bar then mirrors the active state.
  - Stepper tap → click the matching main gram-stepper button, which
    updates the form quantity input and the total.
  - Submit button uses form="..." so the browser submits the main form
    natively — no JS required for the actual cart add.

  Note: <variant-selects> is swapped on variant change by product-info.js,
  which invalidates DOM refs to the gram-stepper / gram-quantity. We
  always look up those refs on demand so stale-ref bugs can't happen.
*/

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.formId = this.dataset.formId;
    this.scopeSelector = `#MainProduct-${this.sectionId}`;

    this.stickyPills = Array.from(this.querySelectorAll('[data-sticky-pill]'));
    this.stickyQty = this.querySelector('[data-sticky-qty]');
    this.stickyTotal = this.querySelector('[data-sticky-total]');
    this.stickySubmit = this.querySelector('[data-sticky-submit]');
    this.stickySubmitLabel = this.querySelector('[data-sticky-submit-label]');

    this.initSlidingPills();
    this.bindStepper();
    this.observeVisibility();
    this.attachMainObservers();
    this.observeCartDrawerClose();
    this.subscribeEvents();

    this.syncFromMain();
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
    this.disconnectMainObservers();
    if (this.optionUnsub) this.optionUnsub();
    if (this.variantChangeUnsub) this.variantChangeUnsub();
    if (this.drawerObserver) this.drawerObserver.disconnect();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  // ---- Main DOM lookups (always fresh) ------------------------------------

  getMainSubmit() {
    return document.querySelector(`#${this.formId} .product-form__submit`);
  }
  getMainPills() {
    return Array.from(
      document.querySelectorAll(`${this.scopeSelector} .product-form__input--pill input[type="radio"]`)
    );
  }
  getMainStepperBtn(step) {
    return document.querySelector(`${this.scopeSelector} .gram-stepper [data-step="${step}"]`);
  }
  getMainQtyDisplay() {
    return document.querySelector(`${this.scopeSelector} [data-qty-display]`);
  }
  getMainTotal() {
    return document.querySelector(`${this.scopeSelector} [data-qty-total]`);
  }

  // ---- Visibility ----------------------------------------------------------

  observeVisibility() {
    const mainSubmit = this.getMainSubmit();
    if (!mainSubmit || typeof IntersectionObserver === 'undefined') return;

    if (this.io) this.io.disconnect();
    this.io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          // Only show when the main submit has scrolled above the viewport,
          // not when the user is still above it (top of page).
          const isAbove = entry.boundingClientRect.bottom < 0;
          const shouldShow = !entry.isIntersecting && isAbove;
          this.toggleVisible(shouldShow);
        }
      },
      { threshold: 0, rootMargin: '0px 0px -10% 0px' }
    );
    this.io.observe(mainSubmit);
  }

  toggleVisible(show) {
    if (show) {
      const wasHidden = this.hasAttribute('hidden');
      this.removeAttribute('hidden');
      // Force reflow so the transition kicks in from translateY(110%).
      void this.offsetWidth;
      this.classList.add('is-visible');
      // Re-position the sliding thumb now that the pills have non-zero
      // dimensions (offsetWidth/Left are 0 when display:none).
      if (wasHidden) requestAnimationFrame(() => this.syncPills());
    } else {
      this.classList.remove('is-visible');
      clearTimeout(this._hideTimer);
      this._hideTimer = setTimeout(() => {
        if (!this.classList.contains('is-visible')) this.setAttribute('hidden', '');
      }, 320);
    }
  }

  // ---- Pills (sliding thumb, mirrors product-variant-pills.js) ------------

  initSlidingPills() {
    const box = this.querySelector('.sticky-atc__pills');
    if (!box) return;
    const labels = this.stickyPills;
    if (!labels.length) return;

    this.stickyPillsBox = box;

    const thumb = document.createElement('span');
    thumb.className = 'sticky-atc__thumb';
    box.insertBefore(thumb, box.firstChild);
    this.stickyThumb = thumb;

    let dragging = false;
    let dragStartX = 0;
    let dragMoved = false;
    let pointerId = null;

    const pillAtX = (clientX) => {
      for (let i = 0; i < labels.length; i++) {
        const r = labels[i].getBoundingClientRect();
        if (clientX >= r.left - 1 && clientX <= r.right + 1) return i;
      }
      const boxRect = box.getBoundingClientRect();
      return clientX < boxRect.left ? 0 : labels.length - 1;
    };

    const visualUpdate = (idx, animate) => {
      if (idx < 0 || idx >= labels.length) return;
      this.placeThumb(labels[idx], animate);
      labels.forEach((l, i) => {
        l.classList.toggle('is-active', i === idx);
        l.setAttribute('aria-pressed', i === idx ? 'true' : 'false');
      });
    };

    const commitActive = (idx) => {
      const sticky = labels[idx];
      if (!sticky || sticky.disabled) return;
      const value = sticky.dataset.value;
      const mainPills = this.getMainPills();
      const target = mainPills.find((r) => r.value === value);
      if (!target || target.disabled || target.checked) return;
      // Snapshot scroll BEFORE the change pipeline runs. The variant fetch
      // path can cause unwanted scroll on mobile (iOS scroll anchoring, the
      // <variant-selects> swap layout shift, deferred gallery callbacks).
      // We restore it after variantChange so the user stays put.
      this._stickyScrollY = window.scrollY;
      mainPills.forEach((r) => { r.checked = false; });
      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
    };

    box.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      const target = e.target.closest('.sticky-atc__pill');
      if (target && target.disabled) return;

      dragging = true;
      dragMoved = false;
      dragStartX = e.clientX;
      pointerId = e.pointerId;
      try { box.setPointerCapture(pointerId); } catch (_) {}
      box.classList.add('dragging');
    });

    box.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      if (Math.abs(e.clientX - dragStartX) > 3) dragMoved = true;
      visualUpdate(pillAtX(e.clientX), false);
    });

    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      box.classList.remove('dragging');
      try { box.releasePointerCapture(pointerId); } catch (_) {}

      const idx = pillAtX(e.clientX);
      visualUpdate(idx, true);
      commitActive(idx);

      setTimeout(() => { dragMoved = false; }, 0);
    };

    box.addEventListener('pointerup', endDrag);
    box.addEventListener('pointercancel', endDrag);

    // Block native click — endDrag already commits the change.
    labels.forEach((label) => {
      label.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    // Reposition thumb on resize so it stays under the active pill.
    this._onResize = () => {
      const active = labels.find((l) => l.classList.contains('is-active'));
      if (active) this.placeThumb(active, false);
      else if (labels[0]) this.placeThumb(labels[0], false);
    };
    window.addEventListener('resize', this._onResize);
  }

  placeThumb(label, animate) {
    if (!label || !this.stickyThumb || !this.stickyPillsBox) return;
    if (!animate) this.stickyPillsBox.classList.add('dragging');
    this.stickyThumb.style.width = label.offsetWidth + 'px';
    this.stickyThumb.style.transform = 'translateX(' + label.offsetLeft + 'px)';
    if (!animate) {
      void this.stickyThumb.offsetWidth;
      this.stickyPillsBox.classList.remove('dragging');
    }
  }

  // ---- Stepper -------------------------------------------------------------

  bindStepper() {
    const minus = this.querySelector('[data-sticky-step="-1"]');
    const plus = this.querySelector('[data-sticky-step="1"]');
    const fire = (step) => {
      const btn = this.getMainStepperBtn(step);
      if (!btn) return;
      btn.click();
      // Defensive sync. The MutationObserver normally catches the change,
      // but if it was bound to a detached node (e.g. variant-selects was
      // swapped) the change wouldn't propagate. Re-read the main DOM.
      setTimeout(() => {
        this.syncQty();
        this.syncTotal();
      }, 30);
    };
    if (minus) minus.addEventListener('click', () => fire('-1'));
    if (plus) plus.addEventListener('click', () => fire('1'));
  }

  // ---- Sync from main UI ---------------------------------------------------

  attachMainObservers() {
    this.disconnectMainObservers();

    const mainTotal = this.getMainTotal();
    if (mainTotal) {
      this.totalObs = new MutationObserver(() => this.syncTotal());
      this.totalObs.observe(mainTotal, { childList: true, characterData: true, subtree: true });
    }

    const mainQty = this.getMainQtyDisplay();
    if (mainQty) {
      this.qtyObs = new MutationObserver(() => this.syncQty());
      this.qtyObs.observe(mainQty, { childList: true, characterData: true, subtree: true });
    }

    const mainSubmit = this.getMainSubmit();
    if (mainSubmit) {
      this.submitObs = new MutationObserver(() => this.syncSubmit());
      this.submitObs.observe(mainSubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
      });
    }
  }

  disconnectMainObservers() {
    if (this.totalObs) { this.totalObs.disconnect(); this.totalObs = null; }
    if (this.qtyObs) { this.qtyObs.disconnect(); this.qtyObs = null; }
    if (this.submitObs) { this.submitObs.disconnect(); this.submitObs = null; }
  }

  subscribeEvents() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    // optionValueSelectionChange fires SYNCHRONOUSLY when a radio toggles —
    // before product-info.js fetches and swaps <variant-selects>. Use it
    // for instant visual sync of the sticky pill active state only.
    this.optionUnsub = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
      requestAnimationFrame(() => this.syncPills());
    });

    // variantChange fires AFTER product-info.js has swapped <variant-selects>
    // for the new variant. The old gram-stepper / [data-qty-display] /
    // [data-qty-total] are now detached. Re-bind observers and re-sync.
    this.variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(() => {
        this.attachMainObservers();
        this.observeVisibility();
        this.syncFromMain();
        this.restoreStickyScroll();
      });
    });
  }

  syncFromMain() {
    this.syncPills();
    this.syncQty();
    this.syncTotal();
    this.syncSubmit();
  }

  syncPills() {
    const mainPills = this.getMainPills();
    const checked = mainPills.find((r) => r.checked);
    const checkedValue = checked ? checked.value : null;

    let activeLabel = null;
    this.stickyPills.forEach((pill) => {
      const isActive = pill.dataset.value === checkedValue;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      if (isActive) activeLabel = pill;

      const matching = mainPills.find((r) => r.value === pill.dataset.value);
      if (matching) pill.disabled = matching.disabled;
    });

    // Move sliding thumb under the active pill — animated when the change
    // came from outside (e.g. main UI), instant on first paint.
    if (activeLabel && this.stickyThumb) {
      const wasUninitialized = this.stickyThumb.style.width === '';
      this.placeThumb(activeLabel, !wasUninitialized);
    }
  }

  syncQty() {
    const main = this.getMainQtyDisplay();
    if (main && this.stickyQty) this.stickyQty.textContent = main.textContent.trim();

    // Mirror the main minus button's disabled state (set when qty <= 1).
    const mainMinus = this.getMainStepperBtn('-1');
    const stickyMinus = this.querySelector('[data-sticky-step="-1"]');
    if (mainMinus && stickyMinus) stickyMinus.disabled = mainMinus.disabled;
  }

  syncTotal() {
    const main = this.getMainTotal();
    if (main && this.stickyTotal) this.stickyTotal.textContent = main.textContent.trim();
  }

  // ---- Cart drawer close → restore submit button --------------------------

  // After a successful add-to-cart, product-form.js sets aria-disabled=true
  // on the submit button (loading state) and removes it in its `finally`
  // block. In some pathological cases the removal can be missed, leaving the
  // button stuck "disabled". When the user closes the cart-drawer we can
  // safely restore the button — at this point loading is definitely done.
  observeCartDrawerClose() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    let wasActive = drawer.classList.contains('active');
    this.drawerObserver = new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      if (wasActive && !isActive) {
        this.unstickMainSubmit();
        this.syncFromMain();
      }
      wasActive = isActive;
    });
    this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  unstickMainSubmit() {
    const main = this.getMainSubmit();
    if (!main) return;
    if (main.getAttribute('aria-disabled') === 'true') {
      main.removeAttribute('aria-disabled');
    }
    main.classList.remove('loading');
    const spinner = main.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  // ---- Scroll lock --------------------------------------------------------

  // Restore the scroll position that was captured when the user tapped a
  // sticky pill. We try multiple frames + a delayed pass to defeat any
  // deferred scroll calls (gallery setTimeout, layout-shift adjustments).
  restoreStickyScroll() {
    if (this._stickyScrollY == null) return;
    const target = this._stickyScrollY;
    this._stickyScrollY = null;

    const restore = () => {
      if (Math.abs(window.scrollY - target) > 1) {
        window.scrollTo(0, target);
      }
    };

    restore();
    requestAnimationFrame(restore);
    setTimeout(restore, 50);
    setTimeout(restore, 150);
  }

  syncSubmit() {
    const mainSubmit = this.getMainSubmit();
    if (!mainSubmit || !this.stickySubmit) return;

    const isDisabled =
      mainSubmit.hasAttribute('disabled') ||
      mainSubmit.getAttribute('aria-disabled') === 'true';
    this.stickySubmit.disabled = isDisabled;

    const mainLabel = mainSubmit.querySelector('span');
    if (mainLabel && this.stickySubmitLabel) {
      const text = mainLabel.textContent.trim();
      if (text) this.stickySubmitLabel.textContent = text;
    }
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
